import axios, {AxiosResponse} from 'axios';
import Cookies from 'js-cookie';
import {getCenterOf, localStorageGet, postMessage} from 'Util/Utilities';
import {resolveApiPath, isAbsoluteExternalUrl} from 'Util/resolveApiPath';
import {MSG_SET_LOADING_CONT_STYLE, MSG_SET_LOADING_STYLE, MSG_SHOW_SNACKBAR, SHOW_LOADING, UNDEF} from './Constants';

export const ACCESS_TOKEN = 'access_token';
export const ACCESS_TOKEN_EXPIRY = 'access_token_expiry';

export function getAccessToken(): string | undefined {
  const cookieToken = Cookies.get(ACCESS_TOKEN);
  if (cookieToken) return cookieToken;

  const lsToken = localStorageGet(ACCESS_TOKEN);
  if (!lsToken) return undefined;

  const expiry = localStorageGet(ACCESS_TOKEN_EXPIRY);
  if (expiry && Date.now() >= expiry * 1000) return undefined;

  return lsToken;
}

let resolvedApiUrl: string | null = null;
let probePromise: Promise<string> | null = null;

async function probeAndGetBaseUrl(): Promise<string> {
  if (resolvedApiUrl !== null) return resolvedApiUrl;
  if (probePromise !== null) return probePromise;

  probePromise = (async () => {
    const primary = process.env.REACT_APP_API_URL || '';
    const fallbacks = (process.env.REACT_APP_API_URL_FALLBACK || '')
      .split(',')
      .map(u => u.trim())
      .filter(Boolean);

    for (const url of [primary, ...fallbacks].filter(Boolean)) {
      try {
        await axios.get(`${url}/_/ping`, {timeout: parseInt(process.env.REACT_APP_API_PING_TIMEOUT_MS || '') || 2000, withCredentials: false});
        resolvedApiUrl = url;
        return url;
      } catch (e: any) {
        // Any HTTP response means the domain is reachable — only skip on network-level failure
        if (e.response) {
          resolvedApiUrl = url;
          return url;
        }
      }
    }

    postMessage(MSG_SHOW_SNACKBAR, {
      key: Date.now(),
      variant: 'error',
      message: 'Unable to reach the server. There may be a network issue on your end. Please refresh the page once the issue is resolved.',
      duration: 10000,
    });
    resolvedApiUrl = primary;
    return primary;
  })();

  return probePromise;
}

function authHeader(): Record<string, string> {
  const token = getAccessToken();
  return token ? {Authorization: `Bearer ${token}`} : {};
}

export async function GET(url: string) {
  const baseUrl = await probeAndGetBaseUrl();
  const path = resolveApiPath(baseUrl, url);
  return axios.get(path, {headers: authHeader(), withCredentials: false});
}
export async function POST(url: string, params: any) {
  const baseUrl = await probeAndGetBaseUrl();
  const path = resolveApiPath(baseUrl, url);
  return axios.post(path, params, {headers: authHeader(), withCredentials: false});
}

export async function PUT(url: string, data: any, headers?: any) {
  const baseUrl = await probeAndGetBaseUrl();
  const path = resolveApiPath(baseUrl, url);
  const reqHeaders = isAbsoluteExternalUrl(url)
    ? {...(headers || {})}
    : {...(headers || {}), ...authHeader()};
  return axios.put(path, data, {headers: reqHeaders, withCredentials: false});
}

export async function PATCH(url: string, data: any, headers?: any) {
  const baseUrl = await probeAndGetBaseUrl();
  const path = resolveApiPath(baseUrl, url);
  const reqHeaders = {...(headers || {}), ...authHeader()};
  return axios.patch(path, data, {headers: reqHeaders, withCredentials: false});
}

export type ResponseCallback = (response?: AxiosResponse<any>, error?: any) => void;

export function showLoading(show = true, style: any = null) {
  postMessage(SHOW_LOADING, show ? 1 : -1, 1);
  style !== null && postMessage(MSG_SET_LOADING_CONT_STYLE, style);
  return true;
}

export function showSnackbar(
  msg: string,
  variant: string,
  durationMilli?: number | undefined,
  actionTitle?: string,
  onAction?: any,
) {
  postMessage(MSG_SHOW_SNACKBAR, {
    key: new Date().getTime(),
    variant: variant,
    message: msg,
    duration: durationMilli,
    actionTitle: actionTitle,
    onAction: onAction,
  });
}

export default class Api {
  private static call(
    method: string,
    url: string,
    params?: any,
    respCallback?: ResponseCallback,
    showLoader: any = true,
    showErrorSnackbar = true,
    showRetry = false,
    headers?: any,
  ) {
    if (showLoader) {
      if (showLoader instanceof HTMLElement) {
        //position loading indicator in center of html element
        const center = getCenterOf(showLoader);
        postMessage(MSG_SET_LOADING_CONT_STYLE, {
          position: 'absolute',
          left: center.x - 30,
          top: center.y - 31,
        });
        postMessage(MSG_SET_LOADING_STYLE, {
          marginLeft: 0,
          marginTop: 0,
        });
      } else {
        postMessage(MSG_SET_LOADING_CONT_STYLE, {});
        postMessage(MSG_SET_LOADING_STYLE, {});
      }
      showLoading();
    }

    const request =
      method === 'GET'
        ? GET(url)
        : method === 'POST'
          ? POST(url, params)
          : method === 'PATCH'
            ? PATCH(url, params, headers)
            : PUT(url, params, headers);
    request.then(
      res => {
        showLoader && showLoading(false);
        respCallback && respCallback(res);

        const data = res && res.data;
        const success = data && data.success;
        const message = data && data.message;

        console.log('API Response:', {
          url,
          raw: res,
          data,
          success,
          message,
        });

        if (success === false && showErrorSnackbar) {
          const actionBtnTxt = showRetry ? 'Retry' : UNDEF;
          const callback = !actionBtnTxt
            ? UNDEF
            : () => {
                Api.call(method, url, params, respCallback, showLoader, showErrorSnackbar, showRetry, headers);
              };
          showSnackbar(message, 'warning', UNDEF, actionBtnTxt, callback);
        }
      },
      err => {
        showLoader && showLoading(false);
        respCallback && respCallback(UNDEF, err);

        if (showErrorSnackbar) {
          const resp = err.response;

          // Don't show network errors (have another snackabr for that)
          if (!resp) return;

          let data = resp && resp.data;
          // handle double data
          if (data && data.data) data = data.data;
          const errMsg = data ? data.error_reason || data.error || data.error_message || data.message : err.message;
          const error = errMsg;
          //  + '\n/' + url;

          const isAuthProblem = resp && resp.status === 401;
          const relog =
            isAuthProblem &&
            (() => {
              Cookies.remove('userid');
              Cookies.remove(ACCESS_TOKEN);
              localStorage.removeItem(ACCESS_TOKEN);
              localStorage.removeItem(ACCESS_TOKEN_EXPIRY);
              window.location.reload();
            });
          const actionBtnTxt = isAuthProblem ? 'Re-Login' : showRetry ? 'Retry' : UNDEF;
          const callback = !actionBtnTxt
            ? UNDEF
            : isAuthProblem
              ? relog
              : () => {
                  Api.call(method, url, params, respCallback, showLoader, showErrorSnackbar, showRetry, headers);
                };
          showSnackbar(error, 'error', UNDEF, actionBtnTxt, callback);
        }
      },
    );
  }

  static GET(
    url: string,
    respCallback?: ResponseCallback,
    showLoading: any = true,
    showErrorSnackbar = true,
    showRetry = false,
  ) {
    Api.call('GET', url, UNDEF, respCallback, showLoading, showErrorSnackbar, showRetry);
  }

  static POST(
    url: string,
    params: any,
    respCallback?: ResponseCallback,
    showLoading: any = true,
    showErrorSnackbar = true,
    showRetry = false,
  ) {
    Api.call('POST', url, params, respCallback, showLoading, showErrorSnackbar, showRetry);
  }

  static PUT(
    url: string,
    data: any,
    respCallback?: ResponseCallback,
    showLoading: any = true,
    showErrorSnackbar = true,
    showRetry = false,
    headers?: any,
  ) {
    Api.call('PUT', url, data, respCallback, showLoading, showErrorSnackbar, showRetry, headers);
  }

  static PATCH(
    url: string,
    data: any,
    respCallback?: ResponseCallback,
    showLoading: any = true,
    showErrorSnackbar = true,
    showRetry = false,
    headers?: any,
  ) {
    Api.call('PATCH', url, data, respCallback, showLoading, showErrorSnackbar, showRetry, headers);
  }
}
